import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import cloneDeep from 'lodash.clonedeep';
import {StyledRecordExpensePage} from './expenses.styled';
import ExpensesRecordTitle from '../expenses/expensesTitle';
import CommonDataForm from './CommonDataForm';
import {BllinkLogger} from '../../common/utils/bllink_loggers';
import { constants } from '../../common/constants/constants';
import {hideLoader, showLoader} from '../../store/common/slice';
import {getAllExpenseTypes, getAllKnownVendors, recordExpenses} from '../../store/expenses/actions';
import {useAccessToken} from '../../common/hooks/useAccessToken';
import NewSupplierPopUp from './newSupplierPopUp';
import GeneralPopUp from '../../common/simple/popUpFunctional';
import ExpensesList from './ExpensesList';
import BllinkTime from '../../common/utils/time';
import {triggerRewardsNotification} from '../rewards/rewardsEvent';
import {GAEvent} from '../../common/google_analytics/google_analytics';
import {isEditableTableValid} from '../../common/shared/statelessMethods';
import PaymentDetailModal from './PaymentDetailsModal';
import {fetchBuildingData} from "../../store/buildings/actions";

export default function RecordExpense({match, history}) {
    const {buildingID} = match.params;
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const client = useAccessToken();
    document.title = t('titles.expenses_record');
    const createExpenseEvent = 'createExpense';

    const vendors = useSelector(state => state.expenses.allKnownVendors);
    const expenseTypes = useSelector(state => state.expenses.expenseTypes);

    const [commonData, setCommonData] = useState({});
    const [expenses, setExpenses] = useState([]);
    const [showAlertPopup, setShowAlertPopup] = useState(false);
    const [showVendorPopup, setShowVendorPopup] = useState(false);
    const [showBankDetailsPopup, setShowBankDetailsPopup] = useState(false);
    const [months, setMonths] = useState([]);
    const [bankOptions, setBankOptions] = useState([]);

    useEffect(async () => {
        if (buildingID != null) {
            const {payload: {currency}} = await dispatch(fetchBuildingData({buildingID}));
            const codes = currency.toUpperCase() === 'MXN' ? constants.bankCodesInMexico : constants.bankCodesInIsrael;
            const country = currency.toUpperCase() === 'MXN' ? 'mexico' : 'israel';
            setBankOptions(codes.map(value => ({ value, label: `${value} - ${t(`common.banks.${country}.${value}`)}` })));
        }
    }, [buildingID]);


    const paymentMethods =  [
        {value: constants.managerPaymentsOptions.cheque, label: t('offline_payments.cheque')},
        {value: constants.managerPaymentsOptions.wire, label: t('offline_payments.wire')},
        {value: constants.managerPaymentsOptions.cash, label: t('offline_payments.cash')},
        {value: constants.managerPaymentsOptions.bankCharge, label: t('payments.bllink_bank_charge')}
    ];

    useEffect(() => {
        if (!client) return;

        getMonthsForDropdown();

        (async function () {
            dispatch(showLoader());
            if (!expenseTypes.length) {
                await dispatch(getAllExpenseTypes());
            }
            if (!vendors.length) {
                await dispatch(getAllKnownVendors(buildingID));
            }
            dispatch(hideLoader());
        })();
    }, [client]);

    useEffect(() => {
        if (expenseTypes.length && vendors.length) {
            const initialExpense = {
                description: '',
                amount: '0',
                invoiceNum: '',
                invoiceFile: {},
                isPeriodChecked: false,
                from: '',
                to: '',
                errors: {
                    amount: {
                        touched: false,
                        valid: false
                    }
                },
                expenseTypeID: expenseTypes[0].value,
                expenseTypes: {all: [...expenseTypes], expenseTypeID: expenseTypes[0].value}
            };
            setExpenses([
                initialExpense
            ]);
            setCommonData({
                firstDate: '',
                selectedVendor: vendors[0],
                frequency: {value: constants.APIContract.expenses.oneTime, label: t('expenses.one_time')},
                selectedPaymentMethod: {value: constants.managerPaymentsOptions.cheque, label: t('offline_payments.cheque')}
            })
        }
    }, [expenseTypes, vendors]);

    const getMonthsForDropdown = () => {
        const monthsOptions = Object.keys(constants.MonthsToText).map(monthInt => ({
            value: `${monthInt}`,
            label: `${t(`common.months.${monthInt}`)}`,
        }));
        setMonths(monthsOptions);
    };

    const changeCommonData = (data, fieldName) => {
        BllinkLogger.info(`${fieldName} changed`, data);
        if (fieldName === 'selectedVendor' && data.value === constants.expenses.addNewVendor) {
            setShowVendorPopup(true);
        }
        setCommonData({
            ...commonData,
            [fieldName]: data
        });
    }

    const changeDate = (fieldName, date) => {
        setCommonData({
            ...commonData,
            [fieldName]: date
        });
    }
    const onVendorAdded = async () => {
        dispatch(showLoader());
        await dispatch(getAllKnownVendors(buildingID));
        dispatch(hideLoader());
        setShowVendorPopup(false);
    }

    const onChangeExpenseType = (expenseType, rowNumber) => {
        BllinkLogger.info(`updateExpenseTypeState`, expenseType.value, rowNumber);
        let expensesCopy = cloneDeep(expenses);
        expensesCopy[rowNumber].expenseTypes.expenseTypeID = expenseType;
        expensesCopy[rowNumber].expenseTypeID = expenseType.value; // for the server
        BllinkLogger.info(`new expenses`, expensesCopy);
        setExpenses(expensesCopy);
    }

    const removeRow = (rowIndex) => {
        let expensesCopy = [...expenses];
        expensesCopy.splice(rowIndex,1);
        BllinkLogger.info(`after change state to less rows`, expensesCopy);
        BllinkLogger.info(`after change state to less rows`, expensesCopy.length);
        setExpenses(expensesCopy);
    }

    const addRow = (evt) => {
        evt.preventDefault();
        let defaultExpenseType = expenseTypes[0].value;
        const row = {
            description: '',
            amount: '0',
            invoiceNum: '',
            invoiceFile: {},
            isPeriodChecked: false,
            from: '',
            to: '',
            errors: {
                amount: {
                    touched: false,
                    valid: false
                }
            },
            expenseTypes: {
                all: [...expenseTypes],
                expenseTypeID: defaultExpenseType
            },
            expenseTypeID: defaultExpenseType // for the server
        };
        setExpenses([
            ...expenses,
            row
        ]);
        BllinkLogger.info(`after change state to more rows`, [
            ...expenses,
            row
        ]);
    }

    const updateExpenses = (rowIndex, field, value, error) => {
        setExpenses(old =>
            old.map((row, index) => {
                if (index === rowIndex) {
                    return {
                        ...old[rowIndex],
                        [field]: value,
                        errors: {
                            ...row.errors,
                            [field]: {
                                touched: true,
                                valid: !error,
                                error: error ? error : undefined
                            }
                        }
                    };
                }
                return row;
            })
        );
    }

    const buildExpensePayload = (values) => {
        // todo, this should be multiplied by the number of rows
        let plans = [];

        const {firstDate, selectedVendor, frequency, selectedPaymentMethod} = commonData;

        let chargeDate = firstDate ?
            BllinkTime.parseDateToBllinkTime(BllinkTime.parseIsraeliDateToDate(BllinkTime.parseDate(firstDate))) :
            BllinkTime.getTimeNow();
        const startMonth = chargeDate;
        let totalAmount = 0;

        for (const expense of values) {
            const day = chargeDate.day;
            let monthlyPayments = [];
            totalAmount += parseFloat(expense.amount);
            if (frequency.value === constants.APIContract.expenses.recurring) {
                for (let i = 0, max = 24; i < max; i++) {
                    const amount = { value: expense.amount, currency: 'ils' };
                    monthlyPayments.push({ chargeDate, amount });
                    chargeDate = BllinkTime.getNextMonth({ ...chargeDate, day });
                }
            } else { // one time payments
                const amount = { value: expense.amount, currency: 'ils' };
                monthlyPayments.push({ chargeDate, amount });
            }

            plans.push({
                description: expense.description ?? null,
                monthlyPayments,
                expenseTypeID: expense.expenseTypeID
            });
        }

        return {
            buildingID: buildingID,
            vendorID: selectedVendor.value,
            paymentMethod: selectedPaymentMethod.value,
            frequency: frequency.value,
            plans: plans,
            startMonth: startMonth,
            totalAmount: totalAmount
        };
    }

    const sendToServer = async (values) => {
        dispatch(showLoader());

        const data = buildExpensePayload(values);
        BllinkLogger.info(`expenses payload`, data);
        reportToGoogle(data);

        let {payload} = await dispatch(recordExpenses(data));
        BllinkLogger.info(`response create expense`, payload);
        dispatch(hideLoader());

        if (payload && payload.success) {
            reportToGoogleAtSuccess(data);
            const {month, year} = data.startMonth;
            alert(t('expenses.added_successfully'));
            triggerRewardsNotification();
            // redirect to monthly expenses
            history.push({pathname: `/${constants.pages.monthlyExpensesUrl}/${buildingID}/${month}/${year}`});
        }
    }

    const reportToGoogle = payload => {
        if (payload.paymentMethod === constants.managerPaymentsOptions.bankCharge) {
            GAEvent(constants.GoogleAnalytics.Events.expenses, createExpenseEvent, 1, 'createBllinkExpenseStart');
            GAEvent(constants.GoogleAnalytics.Events.expenses, createExpenseEvent, payload.totalAmount, 'createBllinkExpenseStartAmount');
        } else {
            GAEvent(constants.GoogleAnalytics.Events.expenses, createExpenseEvent, 1, 'recordOfflineExpenseStart');
            GAEvent(constants.GoogleAnalytics.Events.expenses, createExpenseEvent, payload.totalAmount, 'recordOfflineExpenseStartAmount');
        }
    }

    const reportToGoogleAtSuccess = payload => {
        if (payload.paymentMethod === constants.managerPaymentsOptions.bankCharge) {
            GAEvent(constants.GoogleAnalytics.Events.expenses, createExpenseEvent, 1, 'createBllinkExpenseSuccess');
            GAEvent(constants.GoogleAnalytics.Events.expenses, createExpenseEvent, payload.totalAmount, 'createBllinkExpenseSuccessAmount');
        } else {
            GAEvent(constants.GoogleAnalytics.Events.expenses, createExpenseEvent, 1, 'recordOfflineExpenseSuccess');
            GAEvent(constants.GoogleAnalytics.Events.expenses, createExpenseEvent, payload.totalAmount, 'recordOfflineExpenseSuccessAmount');
        }
    }

    const onRecordExpense = async (evt) => {
        let noVendorHasBeenSelected = '1';
        if ([noVendorHasBeenSelected, constants.expenses.addNewVendor].includes(commonData.selectedVendor.value)) {
            setShowAlertPopup(true);
            return;
        }
        setShowBankDetailsPopup(true);
    }

    return (
        <StyledRecordExpensePage>
            <ExpensesRecordTitle
                buildingID={buildingID}
            />

            <CommonDataForm
                vendors={vendors}
                formData={commonData}
                onDataChanged={changeCommonData}
                paymentMethods={paymentMethods}
                onPaymentDateChanged={date => changeDate('firstDate', date)}
                onCreationDateChanged={date => changeDate('createDate', date)}
            />

            <ExpensesList
                expenseRows={expenses || []}
                months={months}
                onAddLine={addRow}
                onRemoveLineClick={removeRow}
                onChangeExpenseType={onChangeExpenseType}
                setData={updateExpenses}
                onRecordClick={onRecordExpense}
                submitDisable={!isEditableTableValid(expenses)}
            />

            <NewSupplierPopUp
                show={showVendorPopup}
                onModalHide={() => setShowVendorPopup(false)}
                buildingID={buildingID}
                bankOptions={bankOptions}
                onVendorAdded={onVendorAdded}
            />
            <GeneralPopUp
                containerClasses={'header-with-bg'}
                show={showAlertPopup}
                onHideModal={() => setShowAlertPopup(false)}
                text={t('expenses.no_vendor_has_been_selected')}
            />

            <PaymentDetailModal
                show={showBankDetailsPopup}
                onHide={() => setShowBankDetailsPopup(false)}
                expenses={expenses}
                paymentMethods={paymentMethods}
                bankOptions={bankOptions}
                onSave={sendToServer}
            />
        </StyledRecordExpensePage>
    )
}
